// Cache for AI stores to avoid reloading the file multiple times
let aiStoresCache = null

/**
 * Simple CSV parser for single-column data
 * @param {string} csvText - Raw CSV text content
 * @returns {Array<number>} Array of store IDs
 */
const parseSimpleCSV = (csvText) => {
    // Split by lines and filter out empty lines
    const lines = csvText.split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0)
    
    // Skip header row if it contains 'id' and extract numeric values
    const storeIds = lines
        .filter(line => line !== '"id"' && line !== 'id') // Skip header
        .map(line => {
            // Remove quotes if present and parse as integer
            const cleanLine = line.replace(/"/g, '').trim()
            return parseInt(cleanLine, 10)
        })
        .filter(id => !isNaN(id) && id > 0) // Ensure valid positive integers
    
    return storeIds
}

/**
 * Loads AI stores from the CSV file
 * @returns {Promise<Array<number>>} Array of store IDs. 
 * If loading the CSV fails, returns the hardcoded fallback array [602, 412, 1411, 1434, 1512].
 */
export const loadAIStores = async () => {
    // Return cached result if available
    if (aiStoresCache !== null) {
        return aiStoresCache
    }

    try {
        // Fetch the CSV file from the public directory
        const response = await fetch('/ai_stores.csv')
        if (!response.ok) {
            throw new Error(`Failed to fetch AI stores CSV: ${response.status}`)
        }

        const csvText = await response.text()
        
        // Parse CSV content using native JavaScript
        const storeIds = parseSimpleCSV(csvText)

        // Cache the result
        aiStoresCache = storeIds
        
        return storeIds
    } catch (error) {
        console.error('Error loading AI stores:', error)
        // Fallback to the original hardcoded list if CSV loading fails
        return [602, 412, 1411, 1434, 1512]
    }
}

/**
 * Checks if a store ID is an AI test store
 * @param {number|string} storeId - The store ID to check
 * @param {string} storeName - The store name (optional, for fallback check)
 * @returns {Promise<boolean>} True if it's an AI test store
 */
export const isAITestStore = async (storeId, storeName = '') => {
    try {
        // Convert storeId to number if it's a string
        const numericStoreId = typeof storeId === 'string' ? parseInt(storeId, 10) : storeId
        
        // Check if storeId is valid
        if (isNaN(numericStoreId)) {
            console.warn('Invalid store ID provided:', storeId)
            return false
        }
        
        const aiStores = await loadAIStores()
        return aiStores.includes(numericStoreId) || storeName?.includes("AIテスト")
    } catch (error) {
        console.error('Error checking if store is AI test store:', error)
        // Fallback to the original logic
        const numericStoreId = typeof storeId === 'string' ? parseInt(storeId, 10) : storeId
        return [602, 412, 1411, 1434, 1512].includes(numericStoreId) || storeName?.includes("AIテスト")
    }
}
